/**
 * Accessibility Statement — public page (no sign-in required).
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import PageHeader from '../components/PageHeader';
import './AccessibilityPage.css';

const AccessibilityPage = () => {
  const { isSignedIn, isLoaded } = useUser();
  const onAppHome = isLoaded && isSignedIn;
  return (
    <div className="entry-page accessibility-page-stack">
      <PageHeader
        title="Accessibility Statement"
        backTo={onAppHome ? '/' : '/sign-in'}
        backLabel={onAppHome ? '← App home' : '← Sign in'}
      >
        <p className="accessibility-page-intro">
          This page is <strong>public</strong>—you can read it without signing in. For common questions, see{' '}
          <Link to="/faqs">FAQs</Link>. For accepting an invitation, see{' '}
          <Link to="/how-to">Invitation &amp; sign up</Link>.
        </p>
      </PageHeader>

      <div className="accessibility-page-card">
        <p style={{ color: 'var(--cr-text-muted)', marginTop: 0, marginBottom: '1.5rem', fontSize: '0.95rem' }}>
          Last updated: February 23, 2026
        </p>

        <section style={{ marginBottom: '1.5rem' }}>
          <h2 style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>Our Commitment</h2>
          <p>
            Ministry Data Portal is committed to ensuring digital accessibility for people with disabilities.
            We continually improve the user experience for everyone and apply the relevant accessibility standards.
            Accessibility is an ongoing effort and we are dedicated to providing an inclusive experience for all users.
          </p>
        </section>

        <section style={{ marginBottom: '1.5rem' }}>
          <h2 style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>Conformance Status</h2>
          <p>
            We aim to conform to the{' '}
            <a
              href="https://www.w3.org/TR/WCAG21/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--cr-link)', textDecoration: 'underline' }}
            >
              Web Content Accessibility Guidelines (WCAG) 2.1
            </a>{' '}
            Level AA. These guidelines explain how to make web content more accessible to people with a wide
            array of disabilities, including visual, auditory, physical, speech, cognitive, language, learning,
            and neurological disabilities. Conformance with these guidelines also makes web content more usable
            for everyone.
          </p>
          <p style={{ marginTop: '0.5rem' }}>
            <strong>Current status:</strong> We believe this application partially conforms to WCAG 2.1 Level AA.
            &ldquo;Partially conforms&rdquo; means that some parts of the content may not fully conform to all
            success criteria.
          </p>
        </section>

        <section style={{ marginBottom: '1.5rem' }}>
          <h2 style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>Accessibility Features</h2>
          <ul style={{ paddingLeft: '1.25rem', margin: 0, lineHeight: 1.7 }}>
            <li>Keyboard navigation support for primary interactive elements; the bottom navigation &quot;More&quot; menu uses a focus trap and can be closed with Escape</li>
            <li>Visible focus indicators for keyboard users (WCAG 2.4.7)</li>
            <li>Color contrast ratios that meet WCAG AA standards (minimum 4.5:1 for normal text, 3:1 for large text)</li>
            <li>Unique <code>document.title</code> values when you move between major screens (WCAG 2.4.2)</li>
            <li>One primary <code>h1</code> per page where applicable, with section headings in logical order</li>
            <li>Form labels on Add Group Data, Ministry Lead Review, and Inventory are tied to inputs using <code>id</code> and <code>htmlFor</code></li>
            <li>Landmarks and navigation regions (e.g. primary navigation, home sections) include accessible names</li>
            <li>Skip navigation link to bypass repetitive content</li>
            <li>Responsive design that works across screen sizes, devices, and orientations</li>
            <li>Text can be resized up to 200% without loss of content or functionality</li>
            <li>Charts on the Graphs page are paired with text summaries; chart canvases are hidden from assistive technology when those summaries are present</li>
          </ul>
        </section>

        <section style={{ marginBottom: '1.5rem' }}>
          <h2 style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>Known Limitations</h2>
          <p>
            Despite our efforts to ensure accessibility, there may be some limitations. The following is a
            description of known limitations:
          </p>
          <ul style={{ paddingLeft: '1.25rem', margin: '0.5rem 0 0 0', lineHeight: 1.7 }}>
            <li><strong>Charts and graphs:</strong> Canvas-based charts may still be easier to read visually than via a screen reader, even with on-page text summaries. We continue to improve this area.</li>
            <li><strong>Third-party content:</strong> Some content provided by third-party services (such as
              authentication) may not fully meet accessibility standards.</li>
            <li><strong>PDF downloads:</strong> Generated PDF files may not be fully accessible to all assistive
              technologies.</li>
          </ul>
        </section>

        <section style={{ marginBottom: '1.5rem' }}>
          <h2 style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>Compatibility</h2>
          <p>
            Ministry Data Portal is designed to be compatible with the following assistive technologies:
          </p>
          <ul style={{ paddingLeft: '1.25rem', margin: '0.5rem 0 0 0', lineHeight: 1.7 }}>
            <li>Screen readers (VoiceOver on macOS/iOS, TalkBack on Android, NVDA and JAWS on Windows)</li>
            <li>Screen magnification software</li>
            <li>Speech recognition software</li>
            <li>Operating system accessibility features including high contrast modes</li>
          </ul>
          <p style={{ marginTop: '0.5rem' }}>
            The application is built to work with current versions of Chrome, Safari, Firefox, and Edge.
          </p>
        </section>

        <section style={{ marginBottom: '1.5rem' }}>
          <h2 style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>Feedback &amp; Contact</h2>
          <p>
            We welcome your feedback on the accessibility of Ministry Data Portal. If you encounter
            accessibility barriers or have suggestions for improvement, please let us know. We take
            accessibility feedback seriously and aim to respond within 5 business days.
          </p>
          <p style={{ marginTop: '0.75rem' }}>
            <strong>Email:</strong>{' '}
            <a
              href="mailto:anthony@maisonet.com"
              style={{ color: 'var(--cr-link)', textDecoration: 'underline' }}
            >
              anthony@maisonet.com
            </a>
          </p>
          <p style={{ marginTop: '0.5rem' }}>
            When reporting an issue, please include:
          </p>
          <ul style={{ paddingLeft: '1.25rem', margin: '0.25rem 0 0 0', lineHeight: 1.7 }}>
            <li>The page URL or feature where you experienced difficulty</li>
            <li>A description of the problem</li>
            <li>Your device, operating system, browser, and any assistive technology used</li>
          </ul>
        </section>

        <section style={{ marginBottom: '1.5rem' }}>
          <h2 style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>Assessment Approach</h2>
          <p>
            Ministry Data Portal assesses the accessibility of this application through the following methods:
          </p>
          <ul style={{ paddingLeft: '1.25rem', margin: '0.5rem 0 0 0', lineHeight: 1.7 }}>
            <li>Self-evaluation against WCAG 2.1 Level AA success criteria</li>
            <li>Automated testing using accessibility auditing tools</li>
            <li>Manual testing with keyboard-only navigation</li>
            <li>Testing across multiple browsers, devices, and screen sizes</li>
          </ul>
        </section>

        <section>
          <h2 style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>Additional Resources</h2>
          <ul style={{ paddingLeft: '1.25rem', margin: 0, lineHeight: 1.7, listStyle: 'none' }}>
            <li>
              <a
                href="https://www.w3.org/WAI/WCAG21/quickref/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'var(--cr-link)', textDecoration: 'underline' }}
              >
                WCAG 2.1 Quick Reference
              </a>
              {' '}(opens in new tab)
            </li>
            <li style={{ marginTop: '0.35rem' }}>
              <a
                href="https://www.w3.org/WAI/fundamentals/accessibility-intro/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'var(--cr-link)', textDecoration: 'underline' }}
              >
                W3C Introduction to Web Accessibility
              </a>
              {' '}(opens in new tab)
            </li>
          </ul>
        </section>
      </div>
    </div>
  );
};

export default AccessibilityPage;
